import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress, Avatar,
    Modal, ModalDialog, ModalClose, IconButton, Divider,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ReceiptLongRoundedIcon       from '@mui/icons-material/ReceiptLongRounded';
import PointOfSaleRoundedIcon       from '@mui/icons-material/PointOfSaleRounded';
import PaymentsRoundedIcon          from '@mui/icons-material/PaymentsRounded';
import CreditCardRoundedIcon        from '@mui/icons-material/CreditCardRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import LocationOnRoundedIcon        from '@mui/icons-material/LocationOnRounded';
import PersonRoundedIcon            from '@mui/icons-material/PersonRounded';
import AccessTimeRoundedIcon        from '@mui/icons-material/AccessTimeRounded';
import LogoutRoundedIcon            from '@mui/icons-material/LogoutRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import ArrowForwardRoundedIcon      from '@mui/icons-material/ArrowForwardRounded';
import BlockRoundedIcon             from '@mui/icons-material/BlockRounded';
import GavelRoundedIcon             from '@mui/icons-material/GavelRounded';
import BarChartRoundedIcon          from '@mui/icons-material/BarChartRounded';
import RefreshRoundedIcon           from '@mui/icons-material/RefreshRounded';
import AddRoundedIcon               from '@mui/icons-material/AddRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIWorkstationStats,
    APIWorkstationGetAdmins, APIWorkstationStartShift,
    APIWorkstationPhotographersStatus,
    APIWorkstationClaimPhotographer, APIWorkstationReleasePhotographer,
    APIGetPendingSaleNotifications,
    APIProcessSaleNotification, APICancelSaleNotification,
    APIGetProducts, APIGetDiscounts,
    parseApiError,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';
import WorkstationAddOrderModal from './WorkstationAddOrderModal';
import ProcessSaleModal from './ProcessSaleModal';


// ─── Утилиты ──────────────────────────────────────────────────────────────────
const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};

const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

const MONEY_META = {
    transfer: { label: 'Перевод',   color: '#818cf8', bg: 'rgba(129,140,248,0.10)', icon: <CreditCardRoundedIcon  sx={{ fontSize: 14 }} /> },
    cash:     { label: 'Наличные',  color: '#22c55e', bg: 'rgba(34,197,94,0.10)',   icon: <PaymentsRoundedIcon    sx={{ fontSize: 14 }} /> },
    terminal: { label: 'Терминал',  color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  icon: <PointOfSaleRoundedIcon sx={{ fontSize: 14 }} /> },
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.06 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

// ════════════════════════════════════════════════════════════════════
//  Подкомпонент: выбор админа для начала смены
// ════════════════════════════════════════════════════════════════════
function StartShiftPanel({ onStart }) {
    const { token, showToast } = useContext(AppContext);
    const [admins,        setAdmins]        = useState([]);
    const [photographers, setPhotographers] = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [selectedPhIds, setSelectedPhIds] = useState(new Set());
    const [starting,      setStarting]      = useState(false);
    const [step,          setStep]          = useState(1); // 1 = выбор админа, 2 = выбор фотографов

    const load = useCallback(async () => {
        setLoading(true);
        const [adminsData, photogData] = await Promise.all([
            APIWorkstationGetAdmins(token),
            APIWorkstationPhotographersStatus(token),
        ]);
        if (Array.isArray(adminsData?.admins)) setAdmins(adminsData.admins);
        else if (Array.isArray(adminsData))    setAdmins(adminsData);

        if (Array.isArray(photogData?.photographers)) setPhotographers(photogData.photographers);
        setLoading(false);
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const togglePh = (id) => {
        setSelectedPhIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleStart = async () => {
        if (!selectedAdmin) { showToast('Выбери себя из списка', 'warning'); return; }
        setStarting(true);
        const res = await onStart(selectedAdmin, Array.from(selectedPhIds));
        setStarting(false);
        if (res?.ok) {
            setSelectedAdmin(null);
            setSelectedPhIds(new Set());
            setStep(1);
        }
    };

    const freePh   = photographers.filter(p => p.status === 'free');
    const busyPh   = photographers.filter(p => p.status === 'busy');

    return (
        <Box sx={{
            bgcolor: 'var(--joy-palette-background-level1)',
            border: '1px solid var(--joy-palette-divider)',
            borderRadius: 'var(--joy-radius-xl)',
            p: { xs: 2, sm: 3 },
        }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 0.5 }}>
                {step === 1 ? 'Начать смену' : 'С кем работаете'}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                {step === 1
                    ? 'Выберите себя из списка администраторов на сегодня'
                    : 'Отметьте фотографов, чьи чеки будут идти на вас'}
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <MemeLoader />
                </Box>
            ) : step === 1 ? (
                <>
                    {admins.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <BlockRoundedIcon sx={{ fontSize: 36, color: 'var(--joy-palette-text-tertiary)', mb: 1 }} />
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                                Сегодня нет назначенных администраторов
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={1}>
                            {admins.map(a => {
                                const isSel = selectedAdmin === a.id;
                                return (
                                    <Box
                                        key={a.id}
                                        onClick={() => setSelectedAdmin(a.id)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                            px: 2, py: 1.4,
                                            borderRadius: 'var(--joy-radius-lg)',
                                            border: '1px solid',
                                            borderColor: isSel ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                            bgcolor: isSel ? 'rgba(162,38,226,0.08)' : 'var(--joy-palette-background-level2)',
                                            cursor: 'pointer', transition: 'all .15s',
                                        }}
                                    >
                                        <Avatar size="sm" sx={{
                                            width: 36, height: 36, fontSize: 12, fontWeight: 700,
                                            bgcolor: 'rgba(162,38,226,0.15)',
                                            color: 'var(--joy-palette-primary-300)',
                                            border: '1.5px solid var(--joy-palette-primary-500)',
                                        }}>
                                            {getInitials(a.full_name || a.name || '')}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {a.full_name || a.name}
                                            </Typography>
                                            {a.start_time && (
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    с {a.start_time}
                                                </Typography>
                                            )}
                                        </Box>
                                        {isSel && <ArrowForwardRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />}
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}

                    <Button
                        fullWidth
                        disabled={!selectedAdmin}
                        onClick={() => setStep(2)}
                        sx={{
                            mt: 2,
                            background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                            color: '#fff', fontWeight: 700, fontSize: 14,
                            boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                        }}
                    >
                        Далее
                    </Button>
                </>
            ) : (
                <>
                    {photographers.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                                Сегодня нет назначенных фотографов на этой точке
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={1}>
                            {freePh.length > 0 && (
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mt: 0.5 }}>
                                    Свободные
                                </Typography>
                            )}
                            {freePh.map(p => {
                                const isSel = selectedPhIds.has(p.photographer_id);
                                return (
                                    <Box
                                        key={p.photographer_id}
                                        onClick={() => togglePh(p.photographer_id)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                            px: 2, py: 1.2,
                                            borderRadius: 'var(--joy-radius-md)',
                                            border: '1px solid',
                                            borderColor: isSel ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                            bgcolor: isSel ? 'rgba(162,38,226,0.08)' : 'var(--joy-palette-background-level2)',
                                            cursor: 'pointer', transition: 'all .15s',
                                        }}
                                    >
                                        <Box sx={{
                                            width: 18, height: 18,
                                            borderRadius: 4,
                                            border: '2px solid',
                                            borderColor: isSel ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-text-tertiary)',
                                            bgcolor: isSel ? 'var(--joy-palette-primary-500)' : 'transparent',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            {isSel && <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 900, lineHeight: 1 }}>✓</Typography>}
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--joy-palette-text-primary)' }}>
                                                {p.photographer_name}
                                            </Typography>
                                            {p.start_time && (
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    с {p.start_time}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}

                            {busyPh.length > 0 && (
                                <>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mt: 1 }}>
                                        У других админов
                                    </Typography>
                                    {busyPh.map(p => (
                                        <Box
                                            key={p.photographer_id}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                px: 2, py: 1.2,
                                                borderRadius: 'var(--joy-radius-md)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                bgcolor: 'var(--joy-palette-background-level2)',
                                                opacity: 0.5,
                                            }}
                                        >
                                            <Box sx={{ width: 18, height: 18 }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    {p.photographer_name}
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: '#f59e0b' }}>
                                                    У админа {p.current_admin_name}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </>
                            )}
                        </Stack>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                            variant="outlined" color="neutral"
                            onClick={() => setStep(1)}
                            sx={{ fontWeight: 700, fontSize: 13 }}
                        >
                            Назад
                        </Button>
                        <Button
                            sx={{
                                flex: 1,
                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                color: '#fff', fontWeight: 700, fontSize: 14,
                                boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                            }}
                            loading={starting}
                            disabled={starting}
                            onClick={handleStart}
                        >
                            Начать смену {selectedPhIds.size > 0 ? `(${selectedPhIds.size} ф.)` : ''}
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
}

// ════════════════════════════════════════════════════════════════════
//  Подкомпонент: карточка pending-уведомления (быстрое оформление)
// ════════════════════════════════════════════════════════════════════
function PendingSaleCard({ notification, onProcess, onCancel, processing }) {
    const items = notification.items || [];
    const total = parseFloat(notification.total_value || 0);

    return (
        <Box sx={{
            border: '1px solid rgba(245,158,11,0.35)',
            bgcolor: 'rgba(245,158,11,0.06)',
            borderRadius: 'var(--joy-radius-lg)',
            p: 1.8,
            transition: 'all .15s',
        }}>
            {/* Шапка */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
                <Avatar size="sm" sx={{
                    width: 32, height: 32, fontSize: 11, fontWeight: 700,
                    bgcolor: 'rgba(245,158,11,0.15)',
                    color: '#f59e0b',
                    border: '1.5px solid #f59e0b',
                    flexShrink: 0,
                }}>
                    {getInitials(notification.photographer_name)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                        {notification.photographer_name}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                        {fmtTime(notification.date_create)}
                    </Typography>
                </Box>
                <Typography sx={{
                    fontSize: 18, fontWeight: 800,
                    color: '#f59e0b',
                    fontVariantNumeric: 'tabular-nums',
                }}>
                    {fmtMoney(total)} ₽
                </Typography>
            </Box>

            {/* Состав чека */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: notification.comment ? 1 : 1.5 }}>
                {items.map((it, idx) => {
                    const meta = MONEY_META[it.money_type] || MONEY_META.transfer;
                    return (
                        <Chip key={idx} size="sm" variant="soft"
                            startDecorator={meta.icon}
                            sx={{
                                fontSize: 11, fontWeight: 700,
                                bgcolor: meta.bg, color: meta.color,
                            }}
                        >
                            {fmtMoney(it.value)} ₽ · {meta.label}
                        </Chip>
                    );
                })}
            </Box>

            {/* Комментарий */}
            {notification.comment && (
                <Box sx={{
                    bgcolor: 'var(--joy-palette-background-level2)',
                    borderRadius: 'var(--joy-radius-md)',
                    px: 1.5, py: 1, mb: 1.5,
                    borderLeft: '3px solid #f59e0b',
                }}>
                    <Typography sx={{
                        fontSize: 12, color: 'var(--joy-palette-text-primary)',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                        {notification.comment}
                    </Typography>
                </Box>
            )}

            {/* Кнопки */}
            <Stack direction={"row"} sx={{ gap: 1, justifyContent: 'flex-end' }}>
                <Button
                    size="md"
                    startDecorator={<ReceiptLongRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => onProcess(notification)}
                    disabled={processing}
                    sx={{
                        flex: {
                            'xs': 1,
                            'sm': 0.2
                        },
                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        color: '#fff',
                        '&:hover': { boxShadow: '0 4px 12px rgba(34,197,94,0.4)' },
                    }}
                >
                    Оформить чек
                </Button>
                <IconButton
                    size="md" variant="solid" color="danger"
                    onClick={() => onCancel(notification)}
                    disabled={processing}
                    sx={{
                        // bgcolor: 'var(--joy-palette-background-level2)',
                        // border: '1px solid var(--joy-palette-divider)',
                        // '&:hover': { borderColor: 'var(--joy-palette-danger-400)' },
                    }}
                >
                    <BlockRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Stack>
        </Box>
    );
}

// ════════════════════════════════════════════════════════════════════
//  WorkstationDashboard
// ════════════════════════════════════════════════════════════════════
export default function WorkstationDashboard() {
    const { token, showToast, onRefreshTick, activeShift, activeShifts, startShift, endShift } = useContext(AppContext);
    const navigate = useNavigate();

    const [stats,     setStats]     = useState(null);
    const [pending,   setPending]   = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [endingShift, setEndingShift] = useState(false);
    const [confirmEnd, setConfirmEnd] = useState(false);
    const [processingId, setProcessingId] = useState(null);
    const [addOrderOpen, setAddOrderOpen] = useState(false);

    // Справочники для модалки оформления
    const [products,  setProducts]  = useState([]);
    const [discounts, setDiscounts] = useState([]);
    const [processModal, setProcessModal] = useState({ open: false, notification: null, loading: false });

    // ── Загрузка статистики и pending ─────────────────────────────
    const loadAll = useCallback(async () => {
        if (!activeShift) { setLoading(false); return true; }
        setLoading(true);
        const [statsRes, pendingRes, productsRes, discountsRes] = await Promise.all([
            APIWorkstationStats(token),
            APIGetPendingSaleNotifications(token, { status: 'pending' }),
            APIGetProducts(token, { is_active: true }),
            APIGetDiscounts(token, { is_active: true }),
        ]);
        if (statsRes) setStats(statsRes);
        if (Array.isArray(pendingRes)) setPending(pendingRes);
        if (Array.isArray(productsRes)) setProducts(productsRes);
        if (Array.isArray(discountsRes)) setDiscounts(discountsRes);
        setLoading(false);
        return true;
    }, [token, activeShift]);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => onRefreshTick(loadAll), [onRefreshTick, loadAll]);

    // Авто-поллинг pending каждые 30 сек
    useEffect(() => {
        if (!activeShift) return;
        const id = setInterval(() => {
            if (!document.hidden) loadAll();
        }, 10000);
        return () => clearInterval(id);
    }, [activeShift, loadAll]);

    // ── Обработчики ───────────────────────────────────────────────
    const handleEndShift = async () => {
        setEndingShift(true);
        await endShift();
        setEndingShift(false);
        setConfirmEnd(false);
    };

    // Открыть модалку оформления (товары + скидки)
    const handleOpenProcess = (notification) => {
        setProcessModal({ open: true, notification, loading: false });
    };

    // Отправить оформленный чек на сервер.
    // payload = { assignments: [...] } из ProcessSaleModal (новый формат).
    const handleSubmitProcess = async (payload) => {
        const notification = processModal.notification;
        if (!notification) return;
        setProcessModal(p => ({ ...p, loading: true }));
        const res = await APIProcessSaleNotification(token, notification.id, payload);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось оформить чек'), 'danger');
            setProcessModal(p => ({ ...p, loading: false }));
            return;
        }
        const total = (res.orders || []).reduce((s, o) => s + parseFloat(o.value || 0), 0);
        showToast(`Чек оформлен · ${total.toLocaleString('ru-RU')} ₽`);
        setPending(prev => prev.filter(p => p.id !== notification.id));
        setProcessModal({ open: false, notification: null, loading: false });
        loadAll();
    };

    const handleCancelNotif = async (notification) => {
        setProcessingId(notification.id);
        const res = await APICancelSaleNotification(token, notification.id);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось отменить'), 'danger');
        } else {
            showToast('Уведомление отменено');
            setPending(prev => prev.filter(p => p.id !== notification.id));
            loadAll();
        }
        setProcessingId(null);
    };

    // ── Статистические значения ───────────────────────────────────
    const today = stats?.today || {};
    const shift = stats?.shift || activeShift || {};
    const revByType = today.revenue_by_type || {};

    const statBlocks = useMemo(() => ([
        {
            label: 'Выручка', value: fmtMoney(today.revenue_total),
            sub: `${today.orders_count || 0} чек. · точка`,
            color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.08)',
            icon: <AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} />,
        },
        {
            label: 'Перевод', value: fmtMoney(revByType.transfer),
            color: '#818cf8', bg: 'rgba(129,140,248,0.08)',
            icon: <CreditCardRoundedIcon sx={{ fontSize: 18 }} />,
        },
        {
            label: 'Наличные', value: fmtMoney(revByType.cash),
            color: '#22c55e', bg: 'rgba(34,197,94,0.08)',
            icon: <PaymentsRoundedIcon sx={{ fontSize: 18 }} />,
        },
        {
            label: 'Терминал', value: fmtMoney(revByType.terminal),
            color: '#f59e0b', bg: 'rgba(245,158,11,0.08)',
            icon: <PointOfSaleRoundedIcon sx={{ fontSize: 18 }} />,
        },
    ]), [today, revByType]);

    // ── Если смена не начата → экран выбора админа ────────────────
    if (!activeShift) {
        return (
            <motion.div
                variants={pageVariants} initial="initial" animate="animate" exit="exit"
                style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
            >
                <Header />
                <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                            Рабочий аккаунт
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                            Смена не начата
                        </Typography>
                    </Box>
                    <StartShiftPanel onStart={startShift} />
                </Box>
            </motion.div>
        );
    }

    return (
        <motion.div
            variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Заголовок ─────────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                Рабочий аккаунт
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                Сегодня · {new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                            <Button
                                onClick={() => setAddOrderOpen(true)}
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 18 }} />}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700,
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                }}
                            >
                                Добавить чек
                            </Button>
                            <Button
                                startDecorator={<LogoutRoundedIcon sx={{ fontSize: 16 }} />}
                                variant="soft" color="danger"
                                onClick={() => setConfirmEnd(true)}
                            >
                                Завершить смену
                            </Button>
                        </Box>
                    </Box>
                </motion.div>

                {loading && !stats ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={2.5}>

                            {/* ── Активные смены (несколько админов одновременно) ─────── */}
                            {activeShifts.length > 1 && (
                                <motion.div variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        p: 2,
                                    }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 1.5, textTransform: 'uppercase' }}>
                                            Активные смены ({activeShifts.length})
                                        </Typography>
                                        <Stack spacing={1}>
                                            {activeShifts.map(s => (
                                                <Box key={s.id} sx={{
                                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                                    p: 1.5,
                                                    borderRadius: 'var(--joy-radius-md)',
                                                    bgcolor: 'var(--joy-palette-background-level2)',
                                                    border: '1px solid var(--joy-palette-divider)',
                                                }}>
                                                    <Avatar sx={{
                                                        width: 36, height: 36, fontSize: 12, fontWeight: 700,
                                                        bgcolor: 'rgba(162,38,226,0.15)',
                                                        color: 'var(--joy-palette-primary-300)',
                                                        border: '1.5px solid var(--joy-palette-primary-500)',
                                                    }}>
                                                        {getInitials(s.administrator_name)}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                            {s.administrator_name}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                            Фотографов: {s.photographers?.length || 0} · с {fmtTime(s.started_at)}
                                                        </Typography>
                                                    </Box>
                                                    <Button
                                                        size="sm" variant="outlined" color="danger"
                                                        onClick={() => {
                                                            if (window.confirm(`Завершить смену ${s.administrator_name}?`)) {
                                                                endShift(s.id);
                                                            }
                                                        }}
                                                        sx={{ fontSize: 11, fontWeight: 700 }}
                                                    >
                                                        Завершить
                                                    </Button>
                                                </Box>
                                            ))}
                                        </Stack>

                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => {
                                                // Переключаемся на экран добавления новой смены
                                                // (реализуй через состояние showAddShift или роутинг)
                                                showToast('Чтобы добавить ещё одну смену — завершите все текущие и начните заново, либо реализуй роутинг между экранами', 'warning');
                                            }}
                                            sx={{ mt: 1.5, fontWeight: 700, fontSize: 12 }}
                                        >
                                            + Добавить ещё одного админа
                                        </Button>
                                    </Box>
                                </motion.div>
                            )}

                            {/* ── Hero: смена и админ ────────────────────── */}
                            <motion.div variants={fadeUp}>
                                <Box sx={{
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    border: '1px solid var(--joy-palette-primary-500)',
                                    borderRadius: 'var(--joy-radius-xl)',
                                    p: { xs: 2, sm: 2.5 },
                                    background: 'linear-gradient(135deg, rgba(162,38,226,0.10) 0%, rgba(162,38,226,0.02) 100%)',
                                    position: 'relative', overflow: 'hidden',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                        <Avatar sx={{
                                            width: 56, height: 56, fontSize: 18, fontWeight: 800,
                                            bgcolor: 'rgba(162,38,226,0.20)',
                                            color: 'var(--joy-palette-primary-300)',
                                            border: '2px solid var(--joy-palette-primary-500)',
                                        }}>
                                            {getInitials(shift.administrator_name)}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                Админ на смене
                                            </Typography>
                                            <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mt: 0.2 }}>
                                                {shift.administrator_name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <LocationOnRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                                        {shift.location_name}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <AccessTimeRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                                        с {fmtTime(shift.started_at)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <IconButton
                                            variant="soft"
                                            color="primary"
                                            onClick={loadAll}
                                        >
                                            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </motion.div>

                            {/* ── Стат-карточки ─────────────────────────── */}
                            <motion.div variants={fadeUp}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1.5 }}>
                                    {statBlocks.map(s => (
                                        <Box key={s.label} sx={{
                                            px: 2, py: 1.8,
                                            borderRadius: 'var(--joy-radius-lg)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            bgcolor: s.bg,
                                        }}>
                                            <Box sx={{ color: s.color, mb: 0.5 }}>{s.icon}</Box>
                                            <Typography sx={{ fontSize: 20, fontWeight: 800, color: s.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                                {s.value}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                                {s.label}
                                            </Typography>
                                            {s.sub && (
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                    {s.sub}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </motion.div>

                            {/* ── Pending уведомления о продажах ─────────── */}
                            <motion.div variants={fadeUp} style={{ position: 'relative', zIndex: 1 }}>
                                <Box sx={{
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    borderRadius: 'var(--joy-radius-xl)',
                                    p: { xs: 2, sm: 2.5 },
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <NotificationsActiveRoundedIcon sx={{
                                                fontSize: 18,
                                                color: pending.length > 0 ? '#f59e0b' : 'var(--joy-palette-text-tertiary)',
                                            }} />
                                            <Typography sx={{
                                                fontSize: 13, fontWeight: 700,
                                                color: 'var(--joy-palette-text-secondary)',
                                                textTransform: 'uppercase', letterSpacing: '0.04em',
                                            }}>
                                                Ожидают оформления
                                            </Typography>
                                            {pending.length > 0 && (
                                                <Chip size="sm" variant="solid" sx={{
                                                    bgcolor: '#f59e0b', color: '#fff', fontWeight: 800, fontSize: 11,
                                                }}>
                                                    {pending.length}
                                                </Chip>
                                            )}
                                            {loading && (
                                                <CircularProgress
                                                    size="sm"
                                                    sx={{ '--CircularProgress-size': '14px', '--CircularProgress-trackThickness': '2px' }}
                                                />
                                            )}
                                        </Box>
                                        <Typography
                                            onClick={() => navigate('/workstation/orders')}
                                            sx={{
                                                fontSize: 12, fontWeight: 700,
                                                color: 'var(--joy-palette-primary-400)',
                                                cursor: 'pointer',
                                                '&:hover': { color: 'var(--joy-palette-primary-300)' },
                                            }}
                                        >
                                            Все чеки →
                                        </Typography>
                                    </Box>

                                    {pending.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', py: 3 }}>
                                            <ReceiptLongRoundedIcon sx={{ fontSize: 36, color: 'var(--joy-palette-text-tertiary)', mb: 1 }} />
                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                                                Нет новых уведомлений о продажах
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Stack spacing={1.2}>
                                            <AnimatePresence>
                                                {pending.map(n => (
                                                    <motion.div
                                                        key={n.id}
                                                        initial={{ opacity: 0, x: -8 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        exit={{ opacity: 0, x: 8, transition: { duration: 0.18 } }}
                                                    >
                                                        <PendingSaleCard
                                                            notification={n}
                                                            onProcess={handleOpenProcess}
                                                            onCancel={handleCancelNotif}
                                                            processing={processingId === n.id}
                                                        />
                                                    </motion.div>
                                                ))}
                                            </AnimatePresence>
                                        </Stack>
                                    )}
                                </Box>
                            </motion.div>

                            {/* ── Быстрые действия ─────────────────────── */}
                            <motion.div variants={fadeUp} style={{ position: 'relative', zIndex: 2 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: 1.5, position: 'relative', zIndex: 2 }}>
                                    {[
                                        { label: 'Чеки и продажи', icon: <ReceiptLongRoundedIcon sx={{ fontSize: 22 }} />, color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.08)', to: '/workstation/orders' },
                                        { label: 'Штрафы',         icon: <GavelRoundedIcon       sx={{ fontSize: 22 }} />, color: '#ef4444',                            bg: 'rgba(239,68,68,0.08)',  to: '/workstation/fines'  },
                                        { label: 'Статистика',     icon: <BarChartRoundedIcon    sx={{ fontSize: 22 }} />, color: '#06b6d4',                            bg: 'rgba(6,182,212,0.08)',  to: '/workstation/stats'  },
                                    ].map(b => (
                                        <Box
                                            key={b.label}
                                            onClick={() => navigate(b.to)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                px: 2.2, py: 2,
                                                position: 'relative', zIndex: 2,
                                                pointerEvents: 'auto',
                                                borderRadius: 'var(--joy-radius-lg)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                bgcolor: b.bg,
                                                cursor: 'pointer', transition: 'all .15s',
                                                '&:hover': { borderColor: b.color, transform: 'translateY(-1px)' },
                                            }}
                                        >
                                            <Box sx={{ color: b.color }}>{b.icon}</Box>
                                            <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {b.label}
                                            </Typography>
                                            <ArrowForwardRoundedIcon sx={{ fontSize: 16, color: b.color }} />
                                        </Box>
                                    ))}
                                </Box>
                            </motion.div>

                        </Stack>
                    </motion.div>
                )}
            </Box>

            <WorkstationAddOrderModal
                open={addOrderOpen}
                onClose={() => setAddOrderOpen(false)}
                onCreated={loadAll}
                locationId={activeShift?.location?.id || activeShift?.location}
            />

            <ProcessSaleModal
                open={processModal.open}
                notification={processModal.notification}
                products={products}
                discounts={discounts}
                loading={processModal.loading}
                onClose={() => setProcessModal({ open: false, notification: null, loading: false })}
                onSubmit={handleSubmitProcess}
            />

            {/* ── Модалка подтверждения завершения смены ──────────── */}
            <Modal open={confirmEnd} onClose={() => setConfirmEnd(false)}>
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)',
                    maxWidth: 380,
                }}>
                    <ModalClose />
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        Завершить смену?
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                        После завершения вы не сможете создавать чеки и штрафы от имени админа{shift.administrator_name ? ` «${shift.administrator_name}»` : ''}.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" color="neutral" size="sm" onClick={() => setConfirmEnd(false)}>
                            Отмена
                        </Button>
                        <Button color="danger" size="sm" loading={endingShift} onClick={handleEndShift}>
                            Завершить
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </motion.div>
    );
}